/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';
import { SelectChangeEvent } from '@mui/material';

import {
  useCallback,
  useRef,
  useReducer,
  useMemo,
  HTMLInputTypeAttribute,
  useEffect
} from 'react';

export type SelectFieldProps<T extends object> = {
  form: InferReturn<T>;
  fieldName: keyof InferReturn<T>['formState'];
};

export type IArrayFields<TObject extends Record<string, any>> = {
  [K in keyof TObject as TObject[K] extends Array<any> ? K : never]: TObject[K];
};

export type IArrayValue<TArray extends Array<any>> =
  TArray extends Array<infer U> ?
    Partial<U> :
    never;


export type IPair<K extends string, V extends Array<any>> = {
  key: K;
  value: IArrayValue<V>;
};

export type IPairs<T extends Record<string, any>> =
    keyof T extends infer K ?
      K extends string ?
        K extends keyof T ?
          IPair<K, T[K]> : never : never : never;

export interface IArrayPushAction<
  TFormState extends IArrayFields<Record<string, any>>,
  TKey extends keyof IArrayFields<TFormState> & string = keyof IArrayFields<TFormState> & string
> {
  payload: IPairs<TFormState> & { key: TKey };
}


type SetFormStateAction<TState extends object, TKey extends keyof TState = keyof TState> = {
  type: 'SET_FORM_STATE';
  payload: TState;
};

type NextPageAction = {
  type: 'NEXT_PAGE';
};

type PreviousPageAction = {
  type: 'PREVIOUS_PAGE';
};

type RemoveFromArrayAction<TState extends object, TKey extends keyof TState = keyof TState> = {
  type: 'REMOVE_FROM_ARRAY';
  payload: {
    id: string;
    property: TKey;
  };
};

type UpdateArrayItemAction<TState extends IArrayFields<Record<string, any>>, TKey extends keyof IArrayFields<TState> & string = keyof IArrayFields<TState> & string > = {
  type: 'UPDATE_ARRAY_ITEM';
  payload: IPairs<TState> & { key: TKey };
};
type AddItemToArrayAction<TState extends IArrayFields<Record<string, any>>, TKey extends keyof IArrayFields<TState> & string = keyof IArrayFields<TState> & string> = {
  type: 'ADD_ITEM_TO_ARRAY';

  payload: IPairs<TState> & { key: TKey };
};
type IAddItemToArrayAction_Alt<TState extends IArrayFields<Record<string, any>>, TKey extends keyof IArrayFields<TState> & string = keyof IArrayFields<TState> & string, TPayload extends IPairs<TState> = IPairs<TState> > = {
  type: 'ADD_ITEM_TO_ARRAY';
  payload: {
    key: TKey;
    value: IPairs<TState>['value'];
  };

};

type IUpdateArrayItemAction_Alt<TState extends IArrayFields<Record<string, any>>, TKey extends keyof IArrayFields<TState> & string = keyof IArrayFields<TState> & string, TValKey extends keyof IPairs<TState>['value'] = keyof IPairs<TState>['value'] > = {
  type: 'UPDATE_ARRAY_ITEM';
  payload: {
    key: TKey;
    value: IPairs<TState>['value'];
    identifier: TValKey;
  };

};

type IRemoveArrayItemAction_Alt<TState extends IArrayFields<Record<string, any>>, TKey extends keyof IArrayFields<TState> & string = keyof IArrayFields<TState> & string, TPayload extends IPairs<TState> = IPairs<TState> > = {
  type: 'REMOVE_FROM_ARRAY';
  payload: {
    key: TKey;
    identifier?: keyof IPairs<TState>['value'];
    value: string;
  };

};


type IFieldType = HTMLInputTypeAttribute | 'array' | undefined;

export type IUpdateAction<TState extends object, TKey extends keyof TState = keyof TState> = {
  type: 'UPDATE';
  payload: {
    type?: IFieldType;
    property: TKey;
    value: TState[TKey];
  };
};
export type IUpdateBaseAction<TState extends object, TKey extends keyof TState = keyof TState> = {
  type: 'UPDATE';
  payload: {
    type: IFieldType;
    property: TKey;
    value: TState[TKey];
  };
};

export type IResetAction = {
  type: 'RESET';
};

export type ITrimAction = {
  type: 'TRIM';
};

export type IBatchUpdateAction<TState extends object> = {
  type: 'BATCH_UPDATE';
  payload: TState;
};

export type IAction<TState extends object> = (
  IUpdateAction<TState> |
  IResetAction |
  ITrimAction |
  IBatchUpdateAction<TState>
);

export type IBaseAction<TState extends object> = (
  IUpdateBaseAction<TState> |
  IResetAction |
  ITrimAction |
  SetFormStateAction<TState> |
  NextPageAction |
  PreviousPageAction |
  RemoveFromArrayAction<TState> |
  UpdateArrayItemAction<TState> |
  AddItemToArrayAction<TState>
);
export type IAction2<TState extends Record<string, any>> = (
  IUpdateAction<TState> |
  IResetAction |
  ITrimAction |
  SetFormStateAction<TState> |
  NextPageAction |
  PreviousPageAction |
  IRemoveArrayItemAction_Alt<TState> |
  IUpdateArrayItemAction_Alt<TState> |
  IAddItemToArrayAction_Alt<TState> |
  IBatchUpdateAction<TState>
);



export type IFieldConfig<TState, TKeyValue> = {
  label?: string;
  required?: boolean;
  type?: HTMLInputTypeAttribute;
  fnValidate?: (value: TKeyValue, state: TState) => string;
};


export function recursivelyTrimFormStateValues<T>(formState: T): T {
  if (_.isObject(formState)) {
    return _.mapValues(formState, (value) => {
      if (_.isString(value)) {
        return _.trim(value);
      }

      if (_.isArray(value)) {
        return _.map(value, (v) => recursivelyTrimFormStateValues(v));
      }

      if (_.isDate(value)) {
        return value;
      }

      if (_.isObject(value)) {
        return recursivelyTrimFormStateValues(value);
      }

      return value;
    }) as T;
  }

  // Generally shouldn't happen but this function can recurse and be passed a string.
  if (_.isString(formState)) {
    return _.trim(formState) as T;
  }

  return formState;
}



function useGenFieldProps<
  TState extends object,
  TKey extends keyof TState = keyof TState
>(
  formState: TState,
  key2FieldConfig: Partial<Record<TKey, IFieldConfig<TState, TState[TKey]>>>,
  dispatch: React.Dispatch<IAction<TState>>,
  trimOnBlur?: boolean
) {
  return useCallback(
    (fieldKey: keyof typeof key2FieldConfig) => {
      const defaultProps = key2FieldConfig[fieldKey] ?? {
      };
      const errorMessage = defaultProps.fnValidate?.(formState[fieldKey], formState) ?? '';
      const errorObj = errorMessage
        ? {
          error: !!errorMessage,
          helperText: errorMessage
        }
        : {
        };

      return {
        ...defaultProps,
        ...errorObj,
        value: formState[fieldKey],
        onBlur: trimOnBlur
          ? () => dispatch({
            type: 'TRIM'
          })
          : undefined,
        onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          if (_.isString(formState[fieldKey]) && _.isString(event.target.value)) {
            dispatch({
              type: 'UPDATE',
              payload: {
                property: fieldKey,
                // Hack to get around type issue since we know above.
                value: event.target.value as never
              }
            });
          }
        }
      };
    },
    [
      key2FieldConfig,
      formState,
      trimOnBlur,
      dispatch
    ]
  );
}



export function useFormState<TState extends Record<string, any>, TKey extends keyof TState = keyof TState>({
  initialState, key2FieldConfig = {
  }
}: {
  initialState: TState;
  key2FieldConfig?: Partial<Record<TKey, IFieldConfig<TState, TState[TKey]>>>;
}) {
  const fnReducer = useCallback(
    (currentState: TState, action: IAction2<TState>): TState => {
      switch (action.type) {
      // Currently only handles strings and numbers. Need to think through other value types.
        case 'UPDATE':
          if (typeof action.payload.value === typeof currentState[action.payload.property]) {
            return {
              ...currentState,
              [action.payload.property]: action.payload.value
            };
          }
          break;

        case 'TRIM':
          return recursivelyTrimFormStateValues(currentState);

        case 'BATCH_UPDATE':
          return {
            ...action.payload
          };

        case 'RESET':
          const tempState = initialState;
          return {
            ...tempState
          };

        case 'NEXT_PAGE':
          if (currentState.step < currentState.totalSteps) {
            return {
              ...currentState,
              step: currentState.step + 1
            };
          }
          return {
            ...currentState
          };

        case 'PREVIOUS_PAGE':
          if (currentState.step > 1) {
            return {
              ...currentState,
              step: currentState.step - 1
            };
          }
          return {
            ...currentState
          };

        case 'REMOVE_FROM_ARRAY': {
          const valueToMatch = action.payload.value;
          const tempArray = currentState[action.payload.key];

          // if (_.isObject(valueToMatch)) {
          return {
            ...currentState,
            [action.payload.key]: tempArray.filter(<T extends IPairs<TState>>(node: T) => {
              if (action.payload.identifier) {
                return node[action.payload.identifier] !== action.payload.value;
              } else {
                return true;
              }
            })
          };
        }

        case 'ADD_ITEM_TO_ARRAY': {

          const newObject = action.payload.value;
          const tempArray = currentState[action.payload.key];

          if (_.isArray(tempArray)) {
            return {
              ...currentState,
              [action.payload.key]: [
                ...tempArray,
                newObject
              ]
            };
          }

          return {
            ...currentState
          };
        }

        case 'UPDATE_ARRAY_ITEM': {
          const newObject = action.payload.value;
          const tempArray = currentState[action.payload.key];

          if (tempArray[0][action.payload.identifier]) {
            const updatedArray = tempArray.map(<T extends TState[TKey]>(node: T) => {

              if (node[action.payload.identifier] === action.payload.value[action.payload.identifier]) {

                return {
                  ...node,
                  ...newObject as typeof node
                };
              }

              return node;
            });
            if (_.isArray(tempArray)) {
              return {
                ...currentState,
                [action.payload.key]: [...updatedArray]
              };
            }
          }

          return {
            ...currentState
          };
        }
      }
      return currentState;
    },
    [initialState]
  );

  const refInitialState = useRef(initialState);
  const [
    formState,
    dispatch
  ] = useReducer(fnReducer, refInitialState.current);



  useEffect(
    () => {
      if (!_.isEqual(refInitialState.current, initialState)) {
        dispatch({
          type: 'BATCH_UPDATE',
          payload: initialState
        });
        refInitialState.current = initialState;
      }
    },
    [initialState]
  );


  const genFieldProps = useCallback(
    <TThisKey extends keyof typeof key2FieldConfig, TValue extends TState[TThisKey]>(fieldKey: TThisKey) => {
      const value = formState[fieldKey];
      const defaultProps = key2FieldConfig[fieldKey] ?? {
      };
      let errorMessage = defaultProps.fnValidate?.(value, formState);

      if (!errorMessage && (defaultProps.required && !value)) {
        errorMessage = 'Required.';
      }
      const errorObj = errorMessage
        ? {
          error: !!errorMessage,
          validationErrorMessage: errorMessage
        }
        : {
        };

      return {
        ...defaultProps,
        ...errorObj,
        value: formState[fieldKey],
        onBlur: () => dispatch({
          type: 'TRIM'
        }),
        onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent | SelectChangeEvent<unknown>) => {
          if (formState[fieldKey] !== event.target.value) {
            dispatch({
              type: 'UPDATE',
              payload: {
                property: fieldKey,
                // Hack to get around type issue since we know above.
                value: event.target.value as TValue
              }
            });
          }
        }
      };
    },
    [
      key2FieldConfig,
      formState
    ]
  );



  const isValid = useCallback(
    () => {
      const formKeys = _.keys(key2FieldConfig) as Array<keyof typeof key2FieldConfig>;
      return _.isEmpty(formKeys) || !formKeys.some((formKey) => {
        const defaultProps = key2FieldConfig[formKey];

        const value = formState[formKey];
        const reqd = (defaultProps?.required && !value);
        const valid = (defaultProps?.fnValidate && defaultProps.fnValidate(value, formState).length !== 0);
        return (
          reqd || valid
        );
      });
    },
    [
      formState,
      key2FieldConfig
    ]
  );



  return useMemo(
    () => ({
      formState,
      dispatch,
      genFieldProps
      // isValid
    }),
    [
      genFieldProps,
      // isValid,
      formState
    ]
  );
}



export interface IFullFieldConfig<
  TState,
  TKey extends keyof TState = keyof TState
> extends IFieldConfig<TState, TState[TKey]> {
  fieldName: TKey;
  validationErrorMessage: string;
}



export interface IFullFieldConfig<
  TState,
  TKey extends keyof TState = keyof TState
> extends IFieldConfig<TState, TState[TKey]> {
  fieldName: TKey;
  validationErrorMessage: string;
}

export function useBaseFormState<
  TState extends object,
  TKey extends keyof TState = keyof TState
>({
  formState, key2FieldConfig = {
  }
}: {
  formState: TState;
  key2FieldConfig?: Partial<Record<TKey, IFieldConfig<TState, TState[TKey]>>>;
}) {
  const val = useMemo((): Record<TKey, IFullFieldConfig<TState>> => _.mapValues(
    formState,
    (value, key): IFullFieldConfig<TState> => {
      const k = key as TKey;
      const defaultProps = key2FieldConfig[k] ?? {
      };
      const errorMessage = defaultProps.fnValidate?.(value as TState[TKey], formState) ?? '';

      const errorObj = errorMessage
        ? {
          error: !!errorMessage,
          helperText: errorMessage
        }
        : {
        };
      return {
        ...(defaultProps as any),
        name: k,
        validationErrorMessage: errorMessage
      };
    }
  ), [
    formState,
    key2FieldConfig
  ]);

  const genFieldProps = useCallback(
    <TThisKey extends keyof typeof key2FieldConfig, TValue extends TState[TThisKey]>(fieldKey: TThisKey) => {
      const value = formState[fieldKey];
      const defaultProps = key2FieldConfig[fieldKey] ?? {
      };
      let errorMessage = defaultProps.fnValidate?.(value, formState);

      if (defaultProps.required && !value) {
        errorMessage = 'Required.';
      }
      const explodedProps = val[fieldKey];

      const errorObj = explodedProps.validationErrorMessage
        ? {
          error: true,
          helperText: explodedProps.validationErrorMessage
        }
        : {
        };
      return {
        ...explodedProps,
        ...errorObj,
        validationErrorMessage: errorMessage,
        value: formState[fieldKey]
      };
    },
    [
      formState,
      key2FieldConfig,
      val
    ]
  );



  return useMemo(
    () => ({
      formState,
      key2FieldConfig: val,
      genFieldProps
    }),
    [
      formState,
      val,
      genFieldProps
    ]
  );
}



export type InferReturn<T extends object> = ReturnType<typeof useFormState<T>>;
export type InferReturnBase<T extends object> = ReturnType<typeof useBaseFormState<T>>;
